import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllListings } from "../apis/LaneCageApi";
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Container,
  Box,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  Skeleton,
  createTheme,
  ThemeProvider,
  CardMedia,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SortIcon from "@mui/icons-material/Sort";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ImageIcon from "@mui/icons-material/Image";
import { Listing } from "../schemas/listings";
import {
  getBookingOfferHeadline,
  getPublicBookingOffers,
  todayYmd,
} from "../utils/facilityEvents";
import { HALLOWEEN_WALK_IN_YMD } from "../utils/bookingBlackoutDates";

// Custom theme using the provided color palette
const theme = createTheme({
  palette: {
    primary: {
      main: "#7e04de", // Vibrant Purple
    },
    secondary: {
      main: "#068cc1", // Bright Blue
    },
    error: {
      main: "#f27143", // Vibrant Orange
    },
    text: {
      primary: "#3c3759", // Deep Purple/Indigo
      secondary: "#0d6186", // Deep Teal
    },
    background: {
      default: "#ffffff", // Pure White
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          boxShadow: "0 4px 6px rgba(126, 4, 222, 0.1)", // Subtle purple shadow
          borderRadius: 12,
          transition: "transform 0.3s ease, box-shadow 0.3s ease",
          "&:hover": {
            transform: "translateY(-5px)",
            boxShadow: "0 6px 12px rgba(126, 4, 222, 0.2)",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        contained: {
          backgroundImage: "linear-gradient(45deg, #7e04de 30%, #068cc1 90%)",
          color: "#ffffff",
          "&:hover": {
            backgroundImage:
              "linear-gradient(45deg, #7e04de 50%, #068cc1 100%)",
          },
        },
        outlined: {
          borderColor: "#7e04de",
          color: "#7e04de",
          "&:hover": {
            backgroundColor: "rgba(126, 4, 222, 0.04)",
          },
        },
      },
    },
  },
});

// interface Listing {
//   id: string;
//   name: string;
//   description: string;
//   pricePerHour: number;
//   image: string;
//   location?: string;
//   rating?: number;
//   availability?: string;
//   type?: string;
//   size?: string;
// }

const Listings = () => {
  const [listings, setListings] = useState<Listing[]>([]);
  const [filteredListings, setFilteredListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchListings();
  }, []);

  useEffect(() => {
    // Filter and sort logic remains the same
    if (listings.length > 0) {
      let filtered = [...listings];

      if (searchTerm) {
        filtered = filtered.filter(
          (listing) =>
            (listing.name?.toLowerCase() || "").includes(
              searchTerm.toLowerCase()
            ) ||
            (listing.description?.toLowerCase() || "").includes(
              searchTerm.toLowerCase()
            )
        );
      }

      filtered.sort((a, b) => {
        if (sortBy === "price-low") return a.pricePerHour - b.pricePerHour;
        if (sortBy === "price-high") return b.pricePerHour - a.pricePerHour;
        if (sortBy === "name") return a.name.localeCompare(b.name);
        return 0;
      });

      setFilteredListings(filtered);
    }
  }, [listings, searchTerm, sortBy]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const data = await getAllListings(true); // Only fetch active listings for users
      setListings(data.laneOrCages);
      setFilteredListings(data.laneOrCages);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching listings:", err);
      setError("Failed to fetch listings. Please try again later.");
      setErrorSnackbar(true);
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setError("");
    fetchListings();
  };

  const renderSkeletons = () => {
    return Array(6)
      .fill(0)
      .map((_, index) => (
        <Grid item key={`skeleton-${index}`} xs={12} sm={6} md={4}>
          <Card sx={{ borderRadius: 2, boxShadow: 3, height: "100%" }}>
            <Skeleton
              variant="rectangular"
              height={200}
              sx={{ width: "100%", bgcolor: "rgba(126, 4, 222, 0.1)" }}
            />
            <Box sx={{ p: 2 }}>
              <Skeleton
                variant="text"
                height={40}
                width="60%"
                sx={{ mx: "auto", mb: 1, bgcolor: "rgba(126, 4, 222, 0.1)" }}
              />
              <Skeleton
                variant="text"
                height={80}
                sx={{ mb: 1, bgcolor: "rgba(126, 4, 222, 0.1)" }}
              />
              <Skeleton
                variant="text"
                height={30}
                width="40%"
                sx={{ mb: 1, bgcolor: "rgba(126, 4, 222, 0.1)" }}
              />
              <Skeleton
                variant="rectangular"
                height={36}
                sx={{ mt: 2, bgcolor: "rgba(126, 4, 222, 0.1)" }}
              />
            </Box>
          </Card>
        </Grid>
      ));
  };

  return (
    <ThemeProvider theme={theme}>
      <Container sx={{ py: 4, bgcolor: theme.palette.background.default }}>
        <Typography
          variant="h4"
          fontWeight="bold"
          textAlign="center"
          mb={2}
          color="primary"
        >
          Available Listings
        </Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          textAlign="center"
          mb={2}
        >
          Find and book your perfect lane or cage
        </Typography>

        {getPublicBookingOffers().map((event) => (
          <Alert
            key={event.id}
            severity="success"
            sx={{ mb: 2, textAlign: "left" }}
          >
            <Typography fontWeight="bold">{event.name}</Typography>
            <Typography variant="body2">
              {getBookingOfferHeadline(event)}
            </Typography>
          </Alert>
        ))}
        {todayYmd() <= HALLOWEEN_WALK_IN_YMD && (
          <Alert severity="warning" sx={{ mb: 2, textAlign: "left" }}>
            <Typography fontWeight="bold">October 31</Typography>
            <Typography variant="body2">
              Walk-ins are free. Online bookings for lanes and cages are closed.
            </Typography>
          </Alert>
        )}

        {/* Search and Filter Controls */}
        <Box
          sx={{
            mb: 4,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            placeholder="Search by name or description..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="secondary" />
                </InputAdornment>
              ),
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: { xs: "100%", sm: "auto" },
            }}
          >
            <SortIcon sx={{ mr: 1, color: "secondary.main" }} />
            <TextField
              select
              size="small"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              SelectProps={{ native: true }}
              sx={{ minWidth: 150 }}
            >
              <option value="name">Name (A-Z)</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </TextField>
          </Box>
        </Box>

        {/* Results summary */}
        {!loading && !error && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {filteredListings.length}{" "}
              {filteredListings.length === 1 ? "result" : "results"}
              {searchTerm && ` for "${searchTerm}"`}
            </Typography>
          </Box>
        )}

        {/* Error state with retry button */}
        {error && (
          <Box
            sx={{
              textAlign: "center",
              my: 4,
              p: 3,
              border: 1,
              borderColor: "error.light",
              borderRadius: 2,
            }}
          >
            <ErrorOutlineIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h6" color="error" gutterBottom>
              Something went wrong
            </Typography>
            <Typography color="text.secondary" paragraph>
              {error}
            </Typography>
            <Button variant="contained" color="primary" onClick={handleRetry}>
              Try Again
            </Button>
          </Box>
        )}

        {/* Empty search results */}
        {!loading && !error && filteredListings.length === 0 && (
          <Box sx={{ textAlign: "center", my: 4 }}>
            <Typography variant="h6" gutterBottom color="primary">
              No listings found
            </Typography>
            <Typography color="text.secondary" paragraph>
              Try adjusting your search or check back later for new listings.
            </Typography>
            <Button variant="outlined" onClick={() => setSearchTerm("")}>
              Clear Search
            </Button>
          </Box>
        )}

        {/* Listings grid */}
        <Grid container spacing={3} justifyContent="center">
          {loading
            ? renderSkeletons()
            : filteredListings.map((listing) => (
                <Grid item key={listing.id} xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Image Section */}
                    <Box
                      sx={{
                        position: "relative",
                        paddingTop: "56.25%",
                        overflow: "hidden",
                      }}
                    >
                      {listing.image ? (
                        <CardMedia
                          component="img"
                          image={listing.image}
                          alt={listing.name}
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            transition: "transform 0.3s ease-in-out",
                            "&:hover": {
                              transform: "scale(1.05)",
                            },
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(126, 4, 222, 0.1)",
                          }}
                        >
                          <ImageIcon
                            sx={{
                              fontSize: 60,
                              color: "rgba(126, 4, 222, 0.3)",
                            }}
                          />
                        </Box>
                      )}
                    </Box>

                    <CardHeader
                      title={
                        <Typography variant="h6" color="primary">
                          {listing.name}
                        </Typography>
                      }
                      sx={{ pb: 0 }}
                    />

                    <CardContent
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Description */}

                      {listing.size && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          {`Size of ${listing.name}: ${listing.size}`}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {listing.description ||
                          "Experience the best sports facility in town."}
                      </Typography>

                      {/* Price and booking */}
                      <Box sx={{ mt: "auto" }}>
                        <Typography
                          variant="h6"
                          color="primary"
                          gutterBottom
                          fontWeight="bold"
                        >
                          ${listing.pricePerHour}/hour
                        </Typography>
                        <Button
                          variant="contained"
                          fullWidth
                          sx={{
                            mt: 1,
                            py: 1,
                            fontWeight: "bold",
                            textTransform: "none",
                          }}
                          onClick={() => navigate(`/book/${listing.id}`)}
                        >
                          Book Now
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
        </Grid>

        {/* Error notification */}
        <Snackbar
          open={errorSnackbar}
          autoHideDuration={6000}
          onClose={() => setErrorSnackbar(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity="error" onClose={() => setErrorSnackbar(false)}>
            {error}
          </Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  );
};

export default Listings;
